import { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, Rocket, HelpCircle, ChevronDown, MessageSquare } from 'lucide-react';
import { cn } from '../lib/utils';
import { QUICK_START, FAQ } from '../lib/help-content';

interface HelpCenterProps {
  onClose: () => void;
  onContactSupport: () => void;
}

export const HelpCenter = ({ onClose, onContactSupport }: HelpCenterProps) => {
  const [tab, setTab] = useState<'start' | 'faq'>('start');
  // Track the one open FAQ item by "category-index" key (accordion).
  const [openKey, setOpenKey] = useState<string | null>(null);

  return createPortal(
    <div className="fixed inset-0 z-[70] bg-surface backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div
        initial={{ scale: 0.95, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.95, y: 20, opacity: 0 }}
        className="bg-canvas w-full max-w-md rounded-3xl border border-hairline shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-4 shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setTab('start')}
              className={cn(
                'flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest transition-colors',
                tab === 'start' ? 'text-ink' : 'text-ink-2 hover:text-ink-2'
              )}
            >
              <Rocket className="w-3.5 h-3.5" />
              Quick Start
            </button>
            <span className="text-ink-2">·</span>
            <button
              onClick={() => setTab('faq')}
              className={cn(
                'flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest transition-colors',
                tab === 'faq' ? 'text-ink' : 'text-ink-2 hover:text-ink-2'
              )}
            >
              <HelpCircle className="w-3.5 h-3.5" />
              FAQ
            </button>
          </div>
          <button onClick={onClose} className="text-ink-2 hover:text-ink transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body (scrolls) */}
        <div className="px-6 pb-4 overflow-y-auto">
          {/* ── Quick Start ── */}
          {tab === 'start' && (
            <div className="space-y-3">
              <p className="text-[11px] text-ink-2 leading-relaxed pb-1">
                New to Dugout? Here's how to get your team up and running.
              </p>
              {QUICK_START.map((step, i) => (
                <div key={i} className="flex gap-3">
                  <div className="w-7 h-7 shrink-0 rounded-full bg-canvas border border-hairline flex items-center justify-center">
                    <span className="text-xs font-black text-accent-ink">{i + 1}</span>
                  </div>
                  <div className="pt-0.5">
                    <p className="text-xs font-semibold uppercase tracking-wide text-ink">{step.title}</p>
                    <p className="text-[11px] text-ink-2 leading-relaxed mt-1">{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── FAQ ── */}
          {tab === 'faq' && (
            <div className="space-y-5">
              {FAQ.map(group => (
                <div key={group.category}>
                  <p className="text-[9px] font-semibold uppercase tracking-widest text-ink-2 mb-2 px-1">
                    {group.category}
                  </p>
                  <div className="space-y-1.5">
                    {group.items.map((item, i) => {
                      const key = `${group.category}-${i}`;
                      const isOpen = openKey === key;
                      return (
                        <div
                          key={key}
                          className="bg-surface border border-hairline rounded-2xl overflow-hidden"
                        >
                          <button
                            onClick={() => setOpenKey(isOpen ? null : key)}
                            className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
                          >
                            <span className="text-[11px] font-black text-ink leading-snug">{item.q}</span>
                            <ChevronDown
                              className={cn(
                                'w-4 h-4 text-ink-2 shrink-0 transition-transform',
                                isOpen && 'rotate-180'
                              )}
                            />
                          </button>
                          <AnimatePresence initial={false}>
                            {isOpen && (
                              <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: 'auto', opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                className="overflow-hidden"
                              >
                                <p className="px-4 pb-4 text-[11px] text-ink-2 leading-relaxed">
                                  {item.a}
                                </p>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer — hand off to existing support flow */}
        <div className="px-6 py-4 border-t border-hairline bg-surface shrink-0">
          <button
            onClick={onContactSupport}
            className="w-full py-3 bg-ink hover:bg-ink text-surface rounded-xl font-black text-xs uppercase tracking-widest transition-colors flex items-center justify-center gap-2"
          >
            <MessageSquare className="w-4 h-4" />
            Contact Support
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
};
